// src/pages/RecruitmentAnalytics/LaborMarketDashboard.tsx
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { Loader2, TrendingUp, MapPin, Code, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#2575fc', '#6a11cb', '#e67e22', '#2ecc71', '#ff4d4f', '#faad14', '#3498db', '#1abc9c'];

const LaborMarketDashboard: React.FC = () => {
  const [salaryData, setSalaryData] = useState<any[]>([]);
  const [skillsData, setSkillsData] = useState<any[]>([]);
  const [geoData, setGeoData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [salaryRes, skillsRes, geoRes] = await Promise.all([
          api.get('/labor-market/salary-comparison'),
          api.get('/labor-market/skills-demand'),
          api.get('/labor-market/geo-distribution')
        ]);
        setSalaryData(salaryRes.data.yourVacancies || []);
        setSkillsData(skillsRes.data || []);
        setGeoData(geoRes.data || []);
      } catch (err) {
        console.error('Ошибка загрузки данных рынка труда:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div style={styles.center}>
        <Loader2 size={40} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.title}>Анализ рынка труда</h2>

      {/* 1. Сравнение зарплат */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}><TrendingUp size={18} /> Сравнение зарплат (ваши vs рынок)</h3>
        {salaryData.length === 0 ? (
          <p style={styles.emptyText}>Нет данных для сравнения</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Должность</th>
                <th>Ваша средняя з/п</th>
                <th>Рыночная средняя</th>
                <th>Разница</th>
              </tr>
            </thead>
            <tbody>
              {salaryData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.title}</td>
                  <td>{item.yourAvgSalary.toLocaleString()} ₽</td>
                  <td>{item.marketAvgSalary ? `${item.marketAvgSalary.toLocaleString()} ₽` : 'нет данных'}</td>
                  <td style={{ color: item.differencePercent > 0 ? '#2ecc71' : '#e74c3c' }}>
                    {item.differencePercent !== null ? `${item.differencePercent > 0 ? '+' : ''}${item.differencePercent}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 2. Дефицитность навыков */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}><Code size={18} /> Дефицитность навыков</h3>
        {skillsData.length === 0 ? (
          <p style={styles.emptyText}>Нет данных</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={skillsData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="skill" width={120} />
              <Tooltip />
              <Legend />
              <Bar dataKey="vacanciesRequiring" fill="#2575fc" name="Требуется в вакансиях" />
              <Bar dataKey="candidatesHaving" fill="#6a11cb" name="Кандидатов с навыком" />
            </BarChart>
          </ResponsiveContainer>
        )}
        {skillsData.filter(s => s.level === 'Высокий дефицит').length > 0 && (
          <div style={{ marginTop: 12, background: 'rgba(255,165,0,0.1)', padding: '8px 16px', borderRadius: 12 }}>
            <AlertTriangle size={16} color="#e67e22" style={{ marginRight: 8 }} />
            <span>Навыки с высоким дефицитом: {skillsData.filter(s => s.level === 'Высокий дефицит').map(s => s.skill).join(', ')}</span>
          </div>
        )}
      </div>

      {/* 3. Геораспределение кандидатов */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}><MapPin size={18} /> География кандидатов (топ-15 городов)</h3>
        {geoData.length === 0 ? (
          <p style={styles.emptyText}>Нет данных</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={geoData}
                dataKey="count"
                nameKey="city"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ city, count }) => `${city}: ${count}`}
              >
                {geoData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: { padding: '0 20px 20px 20px' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1a1a2e' },
  card: {
    background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)',
borderRadius: 24,
    padding: 20,
    marginBottom: 20,
    boxShadow: '0 8px 20px rgba(0,0,0,0.05)',
    border: '1px solid rgba(255,255,255,0.6)'
  },
  cardTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  emptyText: { textAlign: 'center', padding: 20, color: '#999' }
};

export default LaborMarketDashboard;